import { useState, useEffect } from 'react';
import IconLeftArrow from '../icons/IconLeftArrow';
import IconRightArrow from '../icons/IconRightArrow';
import IconClose from '../icons/IconClose';
import { useLang } from '../hooks/useLanguage';
import { getImageUrl } from '../helpers/images';

export default function Gallery({ show }) {
    const { getI18n, t } = useLang();
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [prevImage, setPrevImage] = useState(null);
    const marketing = show?.marketing || {};
    const { extraImages = [], extraImagesI18n = [] } = marketing;
    const images = (extraImages as string[])
        .map((img: string, idx: number) => ({
            src: getImageUrl(getI18n(img, extraImagesI18n, idx)),
            alt: `${img}_${idx + 1}`,
        }))
        .filter((img) => img.src); // Filter out any null or undefined images

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setIsOpen(true);
        setPrevImage(null);
    };

    const closeLightbox = () => {
        setIsOpen(false);
        setPrevImage(null);
    };

    const nextImage = () => {
        setPrevImage(images[currentIndex]);
        setCurrentIndex((currentIndex + 1) % images.length);
    };

    const prevImageFn = () => {
        setPrevImage(images[currentIndex]);
        setCurrentIndex((currentIndex - 1 + images.length) % images.length);
    };

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === 'ArrowLeft') prevImageFn();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [currentIndex]);

    return (
        <>
            <div className="fireknife-gallery">
                <div className="fireknife-gallery__tns">
                    {(extraImages || []).map((img: string, idx: number) => (
                        <a
                            className="fireknife-gallery__tn"
                            href="#"
                            key={idx}
                            onClick={() => openLightbox(idx)}
                        >
                            <img
                                className="fireknife-gallery__tn-img"
                                src={getImageUrl(getI18n(img, extraImagesI18n, idx))}
                                alt={`${img}_${idx + 1}`}
                            />
                        </a>
                    ))}
                </div>
            </div>

            {isOpen && (
                <div
                    className="fireknife-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${t('image.imageViewer')}: ${images[currentIndex].alt}`}
                >
                    <div className="fireknife-lightbox__overlay" onClick={closeLightbox} />
                    <div className="fireknife-lightbox__img-box" aria-live="polite">
                        {prevImage && (
                            <img
                                className="fireknife-lightbox__img fireknife-lightbox__fade-out"
                                key={prevImage.src}
                                src={prevImage.src}
                                alt={prevImage.alt}
                                onAnimationEnd={() => setPrevImage(null)}
                            />
                        )}
                        <img
                            className="fireknife-lightbox__img fireknife-lightbox__fade-in"
                            key={images[currentIndex].src}
                            src={images[currentIndex].src}
                            alt={images[currentIndex].alt}
                        />
                        <button
                            className="fireknife-lightbox__btn fireknife-lightbox__btn--prev"
                            onClick={prevImageFn}
                            aria-label={t('image.previousImage')}
                        >
                            <IconLeftArrow className="fireknife-lightbox__btn-icon fireknife-lightbox__btn-icon--prev" />
                        </button>
                        <button
                            className="fireknife-lightbox__btn fireknife-lightbox__btn--next"
                            onClick={nextImage}
                            aria-label={t('image.nextImage')}
                        >
                            <IconRightArrow className="fireknife-lightbox__btn-icon fireknife-lightbox__btn-icon--next" />
                        </button>
                        <button
                            className="fireknife-lightbox__btn fireknife-lightbox__btn--close"
                            onClick={closeLightbox}
                            aria-label={t('image.closeGallery')}
                        >
                            <IconClose className="fireknife-lightbox__btn-icon fireknife-lightbox__btn-icon--close" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
